import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

interface ChartData {
  type: string;
  title?: string;
  subtitle?: string;
  description?: string;
  date_range?: {
    start: string;
    end: string;
  };
  daily_execution_counts?: Record<string, any>;
  status_timeline?: Record<string, any>;
  summary?: {
    total_executions: number;
    unique_days_with_activity: number;
    status_distribution: Record<string, number>;
    [key: string]: any;
  };
  // Chart.js format support
  data?: {
    datasets?: any[];
    [key: string]: any;
  };
  options?: {
    plugins?: {
      title?: { text?: string };
      [key: string]: any;
    };
    [key: string]: any;
  };
}

interface ChartRendererProps {
  chartData: ChartData;
}

const COLORS = {
  Completed: "#10B981", // green
  Failed: "#EF4444", // red
  Running: "#3B82F6", // blue
  Pending: "#F59E0B", // yellow
  Cancelled: "#6B7280", // gray
  Unknown: "#8B5CF6", // purple
};

// Timeline execution data structure
interface TimelineExecution {
  execution_uuid: string;
  task_uuid: string;
  task_name: string;
  start_time: string;
  end_time: string | null;
  duration_seconds: number;
  status: string;
  color: string;
}

interface TimelineChartData {
  type: "timeline";
  title: string;
  data: TimelineExecution[];
  time_range: {
    start: string | null;
    end: string | null;
  };
  tasks: string[];
  summary: {
    total_executions: number;
    unique_tasks: number;
    status_distribution: Record<string, number>;
  };
}

// Helper to format duration
const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Timeline Chart Renderer Component
const TimelineChartRenderer: React.FC<{ chartData: TimelineChartData }> = ({
  chartData,
}) => {
  const { title, data, summary, time_range } = chartData;

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="text-gray-500 text-center py-8">
          <p>No execution data available</p>
        </div>
      </div>
    );
  }

  // Parse time
  const parseTime = (isoString: string): number => new Date(isoString).getTime();

  // Find min/max times across all executions
  let minTime = Infinity;
  let maxTime = -Infinity;

  data.forEach((exec) => {
    const start = parseTime(exec.start_time);
    const end = exec.end_time ? parseTime(exec.end_time) : start + exec.duration_seconds * 1000;
    if (start < minTime) minTime = start;
    if (end > maxTime) maxTime = end;
  });

  // Add some padding to the time range (5%)
  const timeRangeMs = maxTime - minTime;
  const padding = timeRangeMs * 0.05;
  minTime -= padding;
  maxTime += padding;

  // Calculate minimum bar width (2% of time range) for visibility
  const minBarWidth = timeRangeMs * 0.02;

  // Convert data to Recharts format using waterfall approach
  // Each row has: offset (transparent bar to start position) + duration (colored bar)
  const chartDataFormatted = data.map((exec) => {
    const startMs = parseTime(exec.start_time);
    const endMs = exec.end_time
      ? parseTime(exec.end_time)
      : startMs + exec.duration_seconds * 1000;
    let durationMs = endMs - startMs;

    // Ensure minimum bar width for visibility
    if (durationMs < minBarWidth) {
      durationMs = minBarWidth;
    }

    // Create unique row label (task_name + short ID for uniqueness)
    const rowLabel = `${exec.task_name} (${exec.execution_uuid.substring(0, 6)})`;

    return {
      rowLabel,
      task_name: exec.task_name,
      execution_uuid: exec.execution_uuid.substring(0, 8),
      status: exec.status,
      color: exec.color,
      // Waterfall: offset is transparent, duration is the actual bar
      offset: startMs - minTime,
      duration: durationMs,
      // Original values for tooltip
      start_time: exec.start_time,
      end_time: exec.end_time,
      duration_seconds: exec.duration_seconds,
    };
  });

  // Format time for axis/tooltip
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp + minTime);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeRange = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload.find((p: any) => p.dataKey === "duration");
      if (!item) return null;
      const d = item.payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
          <p className="font-semibold text-gray-900">{d.task_name}</p>
          <p className="text-gray-600">
            Status:{" "}
            <span style={{ color: d.color }} className="font-medium">
              {d.status}
            </span>
          </p>
          <p className="text-gray-600">
            Start: {new Date(d.start_time).toLocaleString()}
          </p>
          {d.end_time && (
            <p className="text-gray-600">
              End: {new Date(d.end_time).toLocaleString()}
            </p>
          )}
          <p className="text-gray-600">
            Duration: {formatDuration(d.duration_seconds)}
          </p>
          <p className="text-gray-400 text-xs mt-1">ID: {d.execution_uuid}</p>
        </div>
      );
    }
    return null;
  };

  // Calculate chart height based on number of executions (each gets its own row)
  const chartHeight = Math.max(300, Math.min(800, data.length * 35 + 100));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {time_range.start && time_range.end && (
          <p className="text-xs text-gray-500 mt-1">
            {formatTimeRange(parseTime(time_range.start))} —{" "}
            {formatTimeRange(parseTime(time_range.end))}
          </p>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {Object.entries(summary.status_distribution).map(([status, count]) => (
          <div key={status} className="flex items-center gap-1 text-xs">
            <div
              className="w-3 h-3 rounded-sm"
              style={{
                backgroundColor:
                  COLORS[status as keyof typeof COLORS] || COLORS.Unknown,
              }}
            />
            <span className="text-gray-600">
              {status}: {count}
            </span>
          </div>
        ))}
      </div>

      {/* Timeline Chart */}
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "500px" }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartDataFormatted}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 160, bottom: 20 }}
            barCategoryGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
            <XAxis
              type="number"
              domain={[0, maxTime - minTime]}
              tickFormatter={formatTime}
              tick={{ fontSize: 10 }}
              tickCount={6}
            />
            <YAxis
              type="category"
              dataKey="rowLabel"
              tick={{ fontSize: 10 }}
              width={150}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Transparent offset bar (positions the duration bar) */}
            <Bar
              dataKey="offset"
              stackId="timeline"
              fill="transparent"
              barSize={20}
            />
            {/* Duration bar with per-cell colors */}
            <Bar
              dataKey="duration"
              stackId="timeline"
              barSize={20}
              radius={[0, 4, 4, 0]}
            >
              {chartDataFormatted.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Total Executions:</span>
            <span className="ml-1 font-medium">{summary.total_executions}</span>
          </div>
          <div>
            <span className="text-gray-500">Unique Tasks:</span>
            <span className="ml-1 font-medium">{summary.unique_tasks}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Component to handle Chart.js format data
const ChartJSRenderer: React.FC<{ chartData: any }> = ({ chartData }) => {
  const { type, data, options } = chartData;
  const title = options?.plugins?.title?.text || "Chart";
  const datasets = data.datasets || [];

  if (type === "line" || type === "bar") {
    // Convert chart data to recharts format
    const chartData: any[] = [];

    // Handle different data formats
    if (datasets.length > 0 && datasets[0].data) {
      // If data is already in array format (simple array of values)
      if (
        Array.isArray(datasets[0].data) &&
        typeof datasets[0].data[0] === "number"
      ) {
        // Assume x values are indices or need to be provided separately
        const labels = data.labels || [];
        labels.forEach((label: string, index: number) => {
          const dataPoint: any = { date: label };

          datasets.forEach((dataset: any, datasetIndex: number) => {
            dataPoint[`dataset_${datasetIndex}`] = dataset.data[index] || 0;
            dataPoint[`dataset_${datasetIndex}_label`] =
              dataset.label || `Dataset ${datasetIndex + 1}`;
            dataPoint[`dataset_${datasetIndex}_color`] =
              dataset.backgroundColor ||
              dataset.borderColor ||
              `#${Math.floor(Math.random() * 16777215).toString(16)}`;
          });

          chartData.push(dataPoint);
        });
      } else if (
        Array.isArray(datasets[0].data) &&
        typeof datasets[0].data[0] === "object"
      ) {
        // Data is in {x, y} format
        const allXValues = new Set<string>();
        datasets.forEach((dataset: any) => {
          if (dataset.data && Array.isArray(dataset.data)) {
            dataset.data.forEach((point: any) => {
              allXValues.add(point.x);
            });
          }
        });

        // Sort dates
        const sortedDates = Array.from(allXValues).sort();

        // Create data points for each date
        sortedDates.forEach((date) => {
          const dataPoint: any = { date };

          datasets.forEach((dataset: any, index: number) => {
            const point = dataset.data?.find((p: any) => p.x === date);
            dataPoint[`dataset_${index}`] = point ? point.y : 0;
            dataPoint[`dataset_${index}_label`] =
              dataset.label || `Dataset ${index + 1}`;
            dataPoint[`dataset_${index}_color`] =
              dataset.backgroundColor ||
              dataset.borderColor ||
              `#${Math.floor(Math.random() * 16777215).toString(16)}`;
          });

          chartData.push(dataPoint);
        });
      }
    }

    // Check if we have any data to display
    if (chartData.length === 0) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <div className="text-gray-500 text-center py-8">
            <p>No data available to display</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          {type === "bar" ? (
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={(value) => `Date: ${value}`}
                formatter={(value: any, name: string, props: any) => {
                  const datasetIndex = name
                    .replace("dataset_", "")
                    .replace("_label", "");
                  const label = props.payload[`dataset_${datasetIndex}_label`];
                  return [value, label];
                }}
              />
              <Legend />
              {datasets.map((dataset: any, index: number) => (
                <Bar
                  key={index}
                  dataKey={`dataset_${index}`}
                  fill={
                    dataset.backgroundColor ||
                    dataset.borderColor ||
                    `#${Math.floor(Math.random() * 16777215).toString(16)}`
                  }
                  name={dataset.label || `Dataset ${index + 1}`}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={(value) => `Date: ${value}`}
                formatter={(value: any, name: string, props: any) => {
                  const datasetIndex = name
                    .replace("dataset_", "")
                    .replace("_label", "");
                  const label = props.payload[`dataset_${datasetIndex}_label`];
                  return [value, label];
                }}
              />
              <Legend />
              {datasets.map((dataset: any, index: number) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={`dataset_${index}`}
                  stroke={
                    dataset.borderColor ||
                    dataset.backgroundColor ||
                    `#${Math.floor(Math.random() * 16777215).toString(16)}`
                  }
                  strokeWidth={2}
                  name={dataset.label || `Dataset ${index + 1}`}
                  connectNulls={false}
                  dot={{ r: 4 }}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === "scatter") {
    // Convert scatter plot data to timeline format
    const timelineData: any[] = [];

    datasets.forEach((dataset: any) => {
      if (dataset.data && Array.isArray(dataset.data)) {
        dataset.data.forEach((point: any) => {
          timelineData.push({
            date: point.x,
            task: point.y,
            status: dataset.label || "Unknown",
            color:
              dataset.backgroundColor ||
              COLORS[dataset.label as keyof typeof COLORS] ||
              "#6B7280",
          });
        });
      }
    });

    // Sort by date
    timelineData.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <ResponsiveContainer
            width="100%"
            height={Math.max(300, timelineData.length * 40)}
          >
            <BarChart
              data={timelineData}
              layout="horizontal"
              margin={{ top: 20, right: 30, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="category"
                dataKey="date"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
              />
              <YAxis
                type="category"
                dataKey="task"
                tick={{ fontSize: 10 }}
                width={100}
              />
              <Tooltip
                formatter={(_value, _name, props) => [
                  `${props.payload.task}`,
                  `Status: ${props.payload.status}`,
                ]}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Bar dataKey="date" fill="#4169E1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Handle other chart types if needed
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="text-gray-600">
        <p>
          Chart type "{type}" is not yet supported. Please use the custom
          format.
        </p>
      </div>
    </div>
  );
};

const ChartRenderer: React.FC<ChartRendererProps> = ({ chartData }) => {
  // Handle interactive HTML visualizations (professional lineage)
  if (chartData.type === "interactive_html" && typeof chartData.data === "string") {
    const title = chartData.title || "Interactive Visualization";
    const description = chartData.description || "";

    const openVisualization = () => {
      if (typeof chartData.data === 'string') {
        window.open(chartData.data, '_blank');
      }
    };

    return (
      <div className="my-4">
        <div className="bg-white border-t border-x border-gray-200 rounded-t-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mb-4">{description}</p>
          )}
        </div>

        {/* Full-width embedded lineage visualization */}
        <div
          className="border border-gray-200 w-full"
          style={{
            minWidth: '800px',
            paddingLeft: '20px',
            paddingRight: '10px',
            overflow: 'hidden'
          }}
        >
          <iframe
            src={chartData.data}
            title={title}
            className="w-full block"
            style={{
              width: '100%',
              minWidth: '770px',
              height: "500px",
              border: "none"
            }}
            frameBorder="0"
            scrolling="no"
            onError={(e) => {
              console.error("❌ Failed to load interactive lineage:", chartData.data, e);
            }}
          />
        </div>

        {/* Button to open in new tab */}
        <div className="bg-white border-b border-x border-gray-200 rounded-b-lg p-4 text-center">
          <button
            onClick={openVisualization}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg mb-2"
          >
            🚀 Open in New Tab
          </button>
        </div>
      </div>
    );
  }

  // Handle timeline charts (Gantt-style execution visualization)
  if (chartData.type === "timeline") {
    return <TimelineChartRenderer chartData={chartData as unknown as TimelineChartData} />;
  }

  // Handle image type charts (for lineage visualizations)
  if (chartData.type === "image" && typeof chartData.data === "string") {
    const title = chartData.title || "Visualization";
    const description = chartData.description || "";

    // Use the image URL directly - Vite proxy will handle /static/ paths
    const imageUrl: string = chartData.data;

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
                <div className="flex justify-center overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-auto border border-gray-300 rounded-lg shadow-sm cursor-pointer hover:shadow-lg transition-shadow duration-200"
            style={{
              maxHeight: "400px",
              maxWidth: "100%",
              objectFit: "contain"
            }}
            onClick={() => {
              window.open(imageUrl, '_blank', 'noopener,noreferrer');
            }}
            onError={(e) => {
              console.error("❌ Failed to load lineage image:", imageUrl, e);
              (e.target as HTMLImageElement).style.display = "none";
            }}
            title="Click to open full-size image in new window"
          />
        </div>
        <div className="text-center mt-2">
          <p className="text-xs text-gray-500">
            💡 Click image to view full-size in new window
          </p>
        </div>
      </div>
    );
  }

  // Handle chart.js format (data.datasets) vs our custom format
  if (chartData.data && chartData.data.datasets) {
    return <ChartJSRenderer chartData={chartData} />;
  }

  const { type, title, subtitle, daily_execution_counts, summary } = chartData;

  // Convert daily_execution_counts to array format for charts
  const dailyData = daily_execution_counts
    ? Object.entries(daily_execution_counts)
        .map(([date, data]: [string, any]) => ({
          date,
          total: data.total,
          ...data.by_status,
        }))
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];

  // Status distribution data for pie chart
  const statusData = summary?.status_distribution
    ? Object.entries(summary.status_distribution).map(([status, count]) => ({
        name: status,
        value: count,
        color: COLORS[status as keyof typeof COLORS] || COLORS.Unknown,
      }))
    : [];

  const renderExecutionHistoryChart = () => {
    if (!dailyData.length) {
      return (
        <div className="p-4 text-center text-gray-500">
          No execution data available for the selected time period.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Daily Executions Line Chart */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Daily Execution Count
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={(value) => `Date: ${value}`}
                formatter={(value, name) => [value, name]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Total Executions"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Pie Chart */}
        {statusData.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Status Distribution
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, "Executions"]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Stacked Bar Chart by Status */}
        {dailyData.some((d) => Object.keys(d).length > 2) && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Daily Executions by Status
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                {Object.keys(COLORS).map(
                  (status) =>
                    dailyData.some((d) => d[status]) && (
                      <Bar
                        key={status}
                        dataKey={status}
                        stackId="a"
                        fill={COLORS[status as keyof typeof COLORS]}
                        name={status}
                      />
                    ),
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
      {/* Chart Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
        {chartData.date_range && (
          <p className="text-xs text-gray-500 mt-1">
            {chartData.date_range.start} to {chartData.date_range.end}
          </p>
        )}
      </div>

      {/* Chart Content */}
      {type === "execution_history" && renderExecutionHistoryChart()}

      {/* Summary Stats */}
      {summary && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total Executions:</span>
              <span className="ml-1 font-medium">
                {summary.total_executions}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Active Days:</span>
              <span className="ml-1 font-medium">
                {summary.unique_days_with_activity}
              </span>
            </div>
            {summary.unique_tasks && (
              <div>
                <span className="text-gray-500">Unique Tasks:</span>
                <span className="ml-1 font-medium">{summary.unique_tasks}</span>
              </div>
            )}
            {summary.total_executions_unfiltered &&
              summary.total_executions_unfiltered !==
                summary.total_executions && (
                <div>
                  <span className="text-gray-500">Total (Unfiltered):</span>
                  <span className="ml-1 font-medium">
                    {summary.total_executions_unfiltered}
                  </span>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartRenderer;
